"use client"

import HybridMediaModule, {
    type HybridMediaModuleData
} from "@/app/(site)/_components/GCSanityModules/HybridMediaModule"
import { clsx } from "@/src/utils/clsx"
import { type MouseEvent } from "react"

type Variant = "desktop" | "mobile"

type MenuFeaturedContentProps = {
    items: HybridMediaModuleData[] | undefined
    variant: Variant
    onNavigate?: () => void
    /**
     * Mobile only: draw a bottom divider. Set when another content section
     * follows this one. When this is the last section, the footer's top border
     * owns the divider instead, so this stays false to avoid a double border.
     */
    showBottomBorder?: boolean
}

/**
 * Shared featured-media renderer for desktop mega menu (column 4) and mobile
 * drill-down (inline row). Uses aspect-ratio containers so images scale
 * proportionally with the column width on all viewport sizes.
 *
 * Variant layouts per Figma:
 *   - `desktop`: fills the featured content column, scaling with viewport width.
 *   - `mobile`:  horizontal, scroll-snap carousel when multiple items; single
 *                items flush to the full column width.
 */
export default function MenuFeaturedContent({
    items,
    variant,
    onNavigate,
    showBottomBorder
}: MenuFeaturedContentProps) {
    if (!items || items.length === 0) return null

    // A featured block can navigate via the whole-block link, a CTA button in
    // the heading block, or a link-wrapped subheading -- all of which render as
    // anchors. Close the menu whenever the click lands on one; link-less blocks
    // (no anchor hit) leave the menu open.
    const handleItemClick = (event: MouseEvent<HTMLElement>) => {
        if (!onNavigate) return
        if (!(event.target as HTMLElement).closest("a")) return
        onNavigate()
    }

    if (variant === "mobile") {
        const isSingle = items.length === 1
        return (
            <div
                className={clsx(
                    "flex shrink-0 overflow-y-clip",
                    showBottomBorder && "border-b border-b-col-border",
                    !isSingle && "snap-x snap-mandatory overflow-x-auto"
                )}
            >
                {items.map(fc => (
                    <div
                        key={fc._key}
                        className={clsx(
                            "relative shrink-0 border-r border-r-col-border p-12",
                            isSingle ? "w-full" : "w-[80vw] snap-start"
                        )}
                        onClick={handleItemClick}
                    >
                        <div className="relative aspect-[0.7] w-full">
                            <HybridMediaModule
                                data={{ ...fc, height: undefined }}
                                className="!absolute inset-0 !h-full w-full"
                            />
                        </div>
                    </div>
                ))}
            </div>
        )
    }

    return (
        <>
            {items.map(fc => (
                <div
                    key={fc._key}
                    className="relative aspect-[0.56] h-full w-full"
                    onClick={handleItemClick}
                >
                    <HybridMediaModule
                        data={{ ...fc, height: undefined }}
                        className="!absolute inset-0 !h-full w-full"
                    />
                </div>
            ))}
        </>
    )
}
